import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import { message } from 'ant-design-vue'

import App from './App.vue'
import router from './router'
import { getUserInfo } from './api/auth'
import { getMockUserInfo, isMockMode, isDevMode } from './utils/mockData'
import { useAuthStore } from './stores/auth'
import dingtalkUtil from './utils/dingtalk'

// 声明全局变量
declare global {
  interface Window {
    dd: any
    __dingTalkDebugInfo: any[]
  }
}

// 钉钉环境检测 - 使用 UserAgent 检测（更可靠）
const isDingTalk = window.navigator.userAgent.indexOf('DingTalk') > -1

console.log('🔍 环境检测：')
console.log('  UserAgent:', window.navigator.userAgent)
console.log('  是否钉钉环境:', isDingTalk)
console.log('  是否开发模式:', isDevMode())
console.log('  是否Mock模式:', isMockMode())

// 创建 Vue 应用
const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(Antd)

// 全局属性
app.config.globalProperties.$dingtalk = dingtalkUtil

// 初始化认证状态
const authStore = useAuthStore()
authStore.initFromStorage()

// 组织 corpId 配置
const CORP_IDS = {
  primary: 'ding22edd3ac4f8e7fa8a1320dcb25e91351',
  backup: 'dingb78698784bbfd173ee0f45d8e4f7c288',
}

// 登录处理函数 - 钉钉环境才尝试免登
async function handleLogin() {
  // ===== 钉钉环境：尝试免登（支持双组织） =====
  if (isDingTalk) {
    console.log('[登录] 钉钉环境 detected，尝试免登...')
    
    try {
      // 1. 初始化钉钉 JSAPI
      console.log('[钉钉] 初始化 JSAPI...')
      const initResult: any = await dingtalkUtil.init()
      console.log('[钉钉] 初始化结果:', initResult)
      
      if (initResult.success || initResult.fallback) {
        // 2. 先尝试主组织登录
        console.log('[钉钉] 尝试主组织登录...')
        const loginSuccess = await tryLoginWithOrg('primary')
        if (loginSuccess) return true
        
        // 3. 主组织失败，尝试备用组织
        console.log('[钉钉] 主组织登录失败，尝试备用组织...')
        const backupSuccess = await tryLoginWithOrg('backup')
        if (backupSuccess) return true
      }
    } catch (error: any) {
      console.error('[钉钉] 免登失败:', error?.message || error)
      message.error('钉钉登录失败: ' + (error?.message || '未知错误'))
    }
    
    // 钉钉免登失败，继续尝试其他方式
  }
  
  // ===== 非钉钉环境 或 钉钉免登失败：尝试 Mock 模式 =====
  if (isMockMode()) {
    console.log('[Mock] 使用 Mock 用户')
    
    const mockUserInfo = getMockUserInfo()
    
    authStore.setAuth('mock_token_' + Date.now(), mockUserInfo)
    
    console.log('✅ Mock模式：已加载用户', mockUserInfo.udm_person?.name, '(审计日志将显示为钉钉用户)')
    message.success(`Mock登录成功: ${mockUserInfo.udm_person?.name}`)
    
    return true
  }
  
  // ===== 都失败了 =====
  console.log('登录失败：非钉钉环境且 Mock 模式未启用')
  return false
}

/**
 * 尝试使用指定组织登录
 * @returns 登录是否成功
 */
async function tryLoginWithOrg(org: 'primary' | 'backup'): Promise<boolean> {
  const orgLabel = org === 'primary' ? '主组织' : '备用组织'
  const corpId = CORP_IDS[org]
  
  try {
    // 1. 获取授权码（使用对应组织的 corpId）
    console.log(`[钉钉][${orgLabel}] 获取授权码, corpId: ${corpId}`)
    const authCode = await dingtalkUtil.getAuthCode(corpId) as string
    console.log(`[钉钉][${orgLabel}] 授权码获取成功`)

    // 2. 调用后端接口获取用户信息（指定组织）
    console.log(`[钉钉][${orgLabel}] 获取用户信息...`)
    const userInfo = await getUserInfo(authCode, org)
    console.log(`[钉钉][${orgLabel}] 用户信息获取成功`, userInfo)
    
    // 3. 存储用户信息 - 使用后端返回的 session_token
    const sessionToken = userInfo.session_token
    if (!sessionToken) {
      console.error(`[钉钉][${orgLabel}] 未获取到 session_token`)
      return false
    }
    authStore.setAuth(sessionToken, userInfo)
    message.success(`钉钉登录成功（${orgLabel}）`)
    
    // 4. 跳转到首页
    router.push('/')
    return true
  } catch (error: any) {
    const errMsg = error?.response?.data?.detail
    console.error(`[钉钉][${orgLabel}] 登录失败:`, errMsg || error?.message || error)
    // 不弹出 message.error，让外层统一处理
    return false
  }
}

// 执行登录然后挂载应用
handleLogin().then((success) => {
  // 挂载应用
  app.mount('#app')
  
  // 如果登录失败且需要登录，跳转到登录页
  if (!success && !authStore.isAuthenticated) {
    router.push('/login')
  }
})
